import type { AiDecisionDTO } from '@/services/forexApi';

const outcomeLabels: Record<string, string> = {
  running: '分析中', observe: '继续观望', signal_generated: '已生成交易信号',
  shadow_only: '历史观察记录（已退役）', blocked: '未通过检查', failed: '分析未完成',
  expired: '本轮已过期', unknown: '结果待核对',
};

const copyLabels: Record<string, string> = {
  not_following: '尚未跟随此智能体', not_applicable: '本轮无新增跟单任务',
  not_created: '尚未生成跟单指令', queued: '跟单处理中',
  accepted: '指令已接收，等待成交确认', submitted: '已提交，等待成交确认',
  filled: '跟单成交已确认', reported_unverified: 'EA 报告成交，结果待核对',
  applied: '持仓管理已按 Broker 回报执行', processing: '持仓管理执行中',
  rejected: '跟单被拒绝', failed: '跟单失败', expired: '跟单已过期',
  partial: '部分持仓管理步骤已执行，请查看账户', unknown: '跟单结果待核对',
};

export function copyResultLabel(status?: string, accountStatuses?: string[]) {
  if (status === 'partial' && accountStatuses) {
    if (!accountStatuses.length) return '部分执行，账户详情待核对';
    if (!accountStatuses.includes('partial')) return '各账户执行结果不同，请查看账户';
  }
  return copyLabels[status || 'unknown'] || '跟单结果待核对';
}

export function shortAgentLabel(agentId: string) {
  return `智能体 #${agentId.length > 12 ? `${agentId.slice(0, 8)}…` : agentId}`;
}

export function decisionSymbolLabel(symbol: string) {
  return /^[A-Z]{6}$/.test(symbol) ? `${symbol.slice(0, 3)}/${symbol.slice(3)}` : symbol || '品种暂缺';
}

export function decisionPresentation(item: AiDecisionDTO) {
  const outcome = item.analysis_outcome || 'unknown';
  let label = outcomeLabels[outcome] || '结果待核对';
  if (outcome === 'blocked' && ['FAST_HISTORY_INSUFFICIENT', 'FAST_HISTORY_GAP', 'FAST_HISTORY_STALE'].includes(item.reason_code || '')) {
    label = '数据不足';
  }
  // Direction is only actionable when the public projection confirms a signal.
  if (outcome === 'signal_generated') {
    if (item.action === 'BUY') label = '做多';
    if (item.action === 'SELL') label = '做空';
    if (item.action === 'CLOSE') label = '平仓';
  }
  const tone = outcome === 'signal_generated' && item.action === 'BUY' ? 'bg-green-50 text-green-600'
    : outcome === 'signal_generated' && item.action === 'SELL' ? 'bg-red-50 text-red-600'
    : 'bg-purple-50 text-purple-600';

  // These are already-public summaries. No raw model output or stage payloads.
  const reasons = Array.from(new Set([
    item.public_reason, ...(item.reasons || []).map((reason) => reason.summary),
  ].filter((reason): reason is string => typeof reason === 'string' && !!reason.trim())
    .map((reason) => {
      const text = reason.trim();
      // Replace only the known public template; do not invent a next-run promise.
      return text === '本轮价格尚未形成策略要求的入场形态，将在下一根 K 线收盘后继续检查'
        ? '本轮价格尚未形成策略要求的入场形态，继续观望。' : text;
    })));
  const reasonSummary = reasons[0] || '本轮具体原因暂不可用。';
  const signalStatus = item.strategy_trade?.signal_status;
  const signalLabel = signalStatus === 'generated' ? '已生成交易信号'
    : signalStatus === 'not_generated' || signalStatus === 'not_applicable' ? '未生成交易信号'
    : '信号结果待确认';

  return {
    label, tone, reasons, reasonSummary, signalLabel,
    myCopyLabel: copyResultLabel(item.follower_execution?.status, item.my_accounts?.map((account) => account.execution_status) || []),
    symbolLabel: decisionSymbolLabel(item.symbol),
    periodLabel: item.frequency_label ? `${item.frequency_label}周期` : null,
  };
}
